// ---------------------------------------------------------------------------
// cloudinaryUrl — ponto ÚNICO de construção de URL de exibição de imagem de
// perfil (avatar e banner). Onda de imagens de perfil, parte 1 (qualidade).
//
// Antes desta onda TODA superfície renderizava o `secure_url` cru: sem
// `f_auto`, sem `q_auto`, sem largura pedida. O navegador baixava o master e
// reduzia no CSS — e, no caso do avatar, AUMENTAVA (o master tinha 250px e o
// hero do perfil pede 112 CSS px, que num aparelho DPR 3 são 336 px reais).
//
// Regras que este módulo garante:
//  1. A largura pedida é `px CSS × DPR`, com DPR limitado a 3 — acima disso o
//     ganho é invisível e o custo de banda não é.
//  2. A largura é encaixada numa ESCADA (`WIDTH_LADDER`). Pedir a largura
//     exata de cada aparelho fragmentaria o cache do CDN em dezenas de
//     variantes da mesma imagem.
//  3. A transformação de dimensionamento entra DEPOIS de qualquer
//     transformação já presente na URL — é o que preserva o recorte do
//     usuário (parte 2), que precisa acontecer antes do resize.
//  4. URL que não é do Cloudinary passa intacta, com UMA exceção: avatar do
//     Google (`lh3.googleusercontent.com`), que chega do Auth pedindo `s96`
//     e é redimensionável pelo mesmo raciocínio. Quem entrou com Google e
//     nunca subiu foto é a maioria dos perfis — deixar 96px ali seria manter
//     o bug para justamente esses.
//
// ⚠️ NÃO usamos `g_auto` (gravidade por conteúdo). Sem recorte declarado o
// comportamento fica idêntico ao de hoje (centro geométrico), e com recorte
// declarado a gravidade é irrelevante porque a proporção já bate. Ligar
// `g_auto` mudaria o enquadramento de todo avatar já existente de uma vez,
// sem ninguém ter pedido.
// ---------------------------------------------------------------------------

/**
 * Larguras entregues. Qualquer pedido sobe para o degrau seguinte.
 * Mantida curta de propósito: cada degrau é uma variante no cache do CDN.
 */
export const WIDTH_LADDER = [
  64, 96, 128, 192, 256, 384, 512, 768, 1024, 1280, 1536, 2048,
] as const;

/** Teto de DPR. Aparelhos 4× existem; a diferença acima de 3 não se vê. */
export const MAX_DPR = 3;

/**
 * Negociação de formato para superfícies que aceitam imagem ANIMADA.
 *
 * ## A causa raiz do BUG-192, provada nos dois sentidos (06/09/2026)
 *
 * Medido com as URLs REAIS do fundador, pedindo com o `Accept` do iPhone:
 *
 * | asset | id termina em | iPhone recebe | anima? |
 * |---|---|---|---|
 * | avatar (legado) | **`.gif`** | `video/mp4`, 14,7 KB | **sim** |
 * | banner | (sem extensão) | `image/webp` animado, 371 KB | **não** |
 *
 * E a contraprova, trocando só a extensão do MESMO par de assets:
 *
 * | | com `.gif` | sem extensão |
 * |---|---|---|
 * | banner | **`video/mp4`, 81 KB** | `image/webp`, 371 KB |
 * | avatar | **`video/mp4`, 14,7 KB** | `image/webp`, 79 KB |
 *
 * Ou seja: **o WKWebView anima mp4 dentro de `<img>` e NÃO anima o WebP animado
 * que o Cloudinary entrega**, e quem decide entre os dois é a extensão no
 * `public_id`. O avatar dele é anterior ao editor de recorte e guardou o
 * `secure_url` cru, COM extensão; o banner é montado por `buildCroppedUrl`, que
 * reconstrói o id a partir de `parseCloudinaryUrl` — e essa função **remove a
 * extensão** para isolar o `public_id`. O banner nasce sem ela, e por isso
 * congela. Todo banner animado do app tem esse defeito; o avatar do fundador só
 * escapa por ser legado.
 *
 * `f_auto:animated` produz o MESMO mp4 sem depender da extensão (medido: 81.249
 * bytes, byte a byte o do `.gif`).
 *
 * ⚠️ **Fora do WebKit isto é NO-OP, e é o que limita o risco.** Medido com o
 * `Accept` do Chrome de desktop, em master animado e em master estático, os
 * bytes são idênticos aos de `f_auto`. A diferença só aparece em cliente que
 * anuncia `video/*`, que é exatamente onde o defeito mora.
 *
 * ⚠️ **O custo, dito na cara:** num master ESTÁTICO o WebKit passa a receber um
 * mp4 de um quadro (medido: 29 KB contra 47 KB do AVIF). Ele renderiza — é o
 * mesmo caminho que faz o avatar do fundador animar —, mas é menos bytes para a
 * mesma foto, então pode sair mais mole. Quem remove esse custo é o conserto
 * DURÁVEL: gravar o formato do master no upload e devolver a extensão à URL,
 * que é o defeito de verdade. Ver `docs/known-bugs.md` §BUG-192.
 *
 * ⚠️ Por isso a opção é OPT-IN por superfície: só avatar e banner aceitam
 * imagem animada hoje. Capa de livro e capa de lista seguem em `f_auto`.
 */
export const ANIMATED_FORMAT = "f_auto:animated";

const CLOUDINARY_RE = /^(https?:\/\/res\.cloudinary\.com\/([^/]+)\/image\/upload\/)(.+)$/;
const VERSION_SEGMENT_RE = /^v\d+$/;
const GOOGLE_AVATAR_HOST_RE = /^https?:\/\/lh\d+\.googleusercontent\.com\//;

/**
 * Chaves de parâmetro de transformação do Cloudinary. Conjunto FECHADO de
 * propósito: o alternativo — aceitar qualquer `xx_yyy` — engoliria uma pasta
 * de `public_id` chamada `og_images` ou `bo_covers` como se fosse
 * transformação, e o `public_id` sairia truncado.
 */
const TRANSFORM_KEYS = new Set([
  "a", "ar", "b", "bo", "c", "co", "cs", "d", "dl", "dn", "dpr", "du", "e",
  "eo", "f", "fl", "fn", "g", "h", "l", "o", "pg", "q", "r", "so", "t", "u",
  "vc", "w", "x", "y", "z",
]);

const TRANSFORM_PARAM_RE = /^([a-z]{1,3})_[^,/]+$/;

/**
 * `true` quando o segmento tem a FORMA de um componente de transformação
 * (`c_crop,x_10,y_20,w_600,h_600`). Usado SÓ quando não há segmento de versão
 * para servir de marco — ver {@link parseCloudinaryUrl}.
 */
function looksLikeTransform(segment: string): boolean {
  if (!segment || segment.includes(".")) return false;
  return segment.split(",").every((p) => {
    const m = TRANSFORM_PARAM_RE.exec(p);
    return m !== null && TRANSFORM_KEYS.has(m[1]);
  });
}

export interface ParsedCloudinaryUrl {
  /** `https://res.cloudinary.com/<cloud>/image/upload/` */
  prefix: string;
  cloudName: string;
  /** Componentes de transformação já presentes, na ordem. */
  transforms: string[];
  /** `v123/pasta/nome.webp` — versão (quando houver) + public_id + extensão. */
  tail: string;
  /** Sem a extensão e sem o `v123/`. `null` quando não foi possível isolar. */
  publicId: string | null;
  /** Número da versão, sem o `v`. `null` quando a URL não traz versão. */
  version: number | null;
}

/** `true` para uma URL de entrega de imagem do Cloudinary. */
export function isCloudinaryUrl(url: string): boolean {
  return CLOUDINARY_RE.test(url);
}

/**
 * Quebra a URL em prefixo / transformações / cauda.
 *
 * O ponto delicado é separar transformação de public_id: os dois são
 * segmentos de caminho e o public_id pode conter barras (pastas). O marco
 * preferido é o segmento de versão (`v` + dígitos).
 *
 * ⚠️ Mas a versão NEM SEMPRE está lá: os dois construtores de URL de perfil
 * (`imageCrop.ts` no client, `profileImageCrop.ts` nas functions) montam o
 * segmento com `version ? \`v${version}/\` : ""`, então basta a Admin API não
 * devolver o número para a URL publicada sair sem versão. Assumir "sem versão
 * = sem transformação" NÃO era conservador: fazia o resize entrar ANTES do
 * `c_crop` já gravado, e as coordenadas do recorte são do espaço do MASTER.
 * Medido contra o Cloudinary com um recorte `x_200,y_100,w_400,h_400`: na
 * ordem invertida `w_96`, `w_128` e `w_192` respondem **HTTP 400 Invalid crop
 * coordinates** e `w_256`/`w_384` respondem 200 — a MESMA foto carregando numa
 * superfície e quebrando na outra, que é o sintoma que trouxe este bug.
 *
 * Sem versão, portanto, os componentes iniciais são reconhecidos pela FORMA
 * ({@link looksLikeTransform}). O último segmento nunca é consumido: o
 * public_id tem que sobrar com pelo menos um.
 */
export function parseCloudinaryUrl(url: string): ParsedCloudinaryUrl | null {
  const m = CLOUDINARY_RE.exec(url);
  if (!m) return null;

  const [, prefix, cloudName, rest] = m;
  const segments = rest.split("/");
  const versionIdx = segments.findIndex((s) => VERSION_SEGMENT_RE.test(s));

  let splitAt = versionIdx;
  if (splitAt === -1) {
    splitAt = 0;
    while (splitAt < segments.length - 1 && looksLikeTransform(segments[splitAt])) {
      splitAt += 1;
    }
  }

  const transforms = segments.slice(0, splitAt);
  const tailSegments = segments.slice(splitAt);
  const version = versionIdx === -1 ? null : Number(tailSegments[0].slice(1));

  const idSegments = versionIdx === -1 ? tailSegments : tailSegments.slice(1);
  const rawId = idSegments.join("/");
  const publicId = rawId ? rawId.replace(/\.[a-z0-9]+$/i, "") : null;

  return {
    prefix,
    cloudName,
    transforms,
    tail: tailSegments.join("/"),
    publicId,
    version: Number.isFinite(version) ? version : null,
  };
}

/** DPR efetivo: inteiro entre 1 e {@link MAX_DPR}. */
export function resolveDpr(explicit?: number): number {
  const raw =
    explicit ??
    (typeof window !== "undefined" ? window.devicePixelRatio : undefined) ??
    1;
  if (!Number.isFinite(raw) || raw <= 0) return 1;
  return Math.min(MAX_DPR, Math.max(1, Math.round(raw)));
}

/** Encaixa uma largura no degrau imediatamente superior da escada. */
export function snapWidth(target: number): number {
  const wanted = Math.max(1, Math.ceil(target));
  return WIDTH_LADDER.find((w) => w >= wanted) ?? WIDTH_LADDER[WIDTH_LADDER.length - 1];
}

export interface CloudinaryUrlOptions {
  /** Largura CSS da caixa onde a imagem será renderizada. */
  width: number;
  /**
   * Altura CSS. Com `fit: "fill"` força a proporção final (avatar quadrado).
   * Omitida, a altura fica livre.
   */
  height?: number;
  /** Sobrescreve o DPR — use nos testes e na exportação de card. */
  dpr?: number;
  /**
   * `fill` recorta para caber exatamente em width×height.
   * `limit` só reduz, nunca amplia, e preserva a proporção.
   * Default: `fill` quando há altura, `limit` quando não há.
   */
  fit?: "fill" | "limit";
  /** Gravidade do `c_fill`. Sem valor, o Cloudinary usa o centro. */
  gravity?: string;
  /**
   * Valor de `q_` do Cloudinary. Default `auto:good`, que é o certo para
   * imagem exibida em escala 1:1 ou reduzida.
   *
   * ⚠️ Superfície que AMPLIA a imagem precisa de mais bytes, e não menos: o
   * upscale multiplica o artefato de compressão junto com o pixel. É o caso do
   * banner do perfil — ver `bannerImage.ts`.
   */
  quality?: string;
  /**
   * `true` nas superfícies que aceitam imagem ANIMADA (avatar e banner). Troca
   * `f_auto` por {@link ANIMATED_FORMAT}, sem o qual o WebKit recebe um WebP
   * animado que ele não anima. Opt-in de propósito — ver o docblock da
   * constante para a medição e para o custo.
   */
  animatedAware?: boolean;
  /**
   * `true` pede o QUADRO PARADO (`pg_1`, primeira página/quadro) de uma imagem
   * animada. Para superfícies de lista que mostram arte de OUTRA conta — o
   * cartão de "Leitores parecidos" (banner como quadro parado, decisão E5) —,
   * onde 3 a 5 animações numa fileira pesam no WebKit (R13). Ignora
   * `animatedAware`: quadro parado nunca precisa do caminho de vídeo.
   */
  stillFrame?: boolean;
}

/**
 * URL de exibição otimizada. Passa intacta o que não souber tratar
 * (`preset:`, `data:`, `blob:`, Lottie, host desconhecido).
 */
export function cloudinaryImageUrl(
  url: string | null | undefined,
  options: CloudinaryUrlOptions,
): string {
  if (!url) return "";
  if (
    url.startsWith("preset:") ||
    url.startsWith("data:") ||
    url.startsWith("blob:")
  ) {
    return url;
  }

  const dpr = resolveDpr(options.dpr);
  const width = snapWidth(options.width * dpr);

  if (GOOGLE_AVATAR_HOST_RE.test(url)) {
    return googleAvatarUrl(url, width);
  }

  const parsed = parseCloudinaryUrl(url);
  if (!parsed) return url;

  // Já passou por aqui: `f_auto` é a assinatura deste helper. Reaplicar
  // empilharia um segundo resize sobre o primeiro — mais lento e, se o
  // primeiro tiver sido menor, irreversivelmente pior.
  if (parsed.transforms.some((t) => t.includes("f_auto"))) return url;

  const fit = options.fit ?? (options.height ? "fill" : "limit");
  const parts: string[] = [`c_${fit}`, `w_${width}`];

  if (options.height) {
    const ratio = options.height / options.width;
    parts.push(`h_${Math.max(1, Math.round(width * ratio))}`);
  }
  if (fit === "fill" && options.gravity) parts.push(`g_${options.gravity}`);
  if (options.stillFrame) parts.push("pg_1");
  parts.push(
    options.animatedAware && !options.stillFrame ? ANIMATED_FORMAT : "f_auto",
    `q_${options.quality ?? "auto:good"}`,
  );

  const transforms = [...parsed.transforms, parts.join(",")];
  return `${parsed.prefix}${transforms.join("/")}/${parsed.tail}`;
}

/**
 * URL do original, sem NENHUMA transformação — inclusive sem o recorte já
 * gravado. É o que o editor de recorte carrega para permitir reenquadrar sem
 * novo upload.
 */
export function cloudinaryOriginalUrl(
  cloudName: string,
  publicId: string,
  version?: number | null,
): string {
  const v = version ? `v${version}/` : "";
  return `https://res.cloudinary.com/${cloudName}/image/upload/${v}${publicId}`;
}

/**
 * Avatar do Google Auth. A URL termina em `=s96-c` (96px, recortado) ou vem
 * sem sufixo; trocar o número é a forma documentada de pedir outro tamanho.
 */
function googleAvatarUrl(url: string, width: number): string {
  const size = Math.min(width, 512); // teto do que o Google serve com folga
  const base = url.replace(/=[-\w]+$/, "");
  return `${base}=s${size}-c`;
}

/**
 * Largura em px que uma classe utilitária do Tailwind produz — usada para o
 * componente descobrir sozinho o tamanho que está pedindo.
 *
 * Existe para evitar a alternativa: uma prop numérica em cada um dos ~20 call
 * sites, que sairia de sincronia com a classe ao lado dela na primeira vez
 * que alguém mudasse só uma das duas. Reconhece `h-28`, `h-[26px]` e
 * `size-10`; devolve `null` para `h-full` e para o que não souber ler, e aí
 * quem chama decide o padrão.
 */
export function pxFromSizeClassName(className: string | undefined): number | null {
  if (!className) return null;

  const arbitrary = /(?:^|\s)(?:h|w|size)-\[(\d+(?:\.\d+)?)px\]/.exec(className);
  if (arbitrary) return Math.round(Number(arbitrary[1]));

  const scale = /(?:^|\s)(?:h|w|size)-(\d+(?:\.\d+)?)(?:\s|$)/.exec(className);
  if (scale) return Math.round(Number(scale[1]) * 4); // escala do Tailwind: 1 = 0.25rem = 4px

  return null;
}
